// frontend/src/components/billing/BillingControls.jsx
//
// The Plans page's transacting half, in the two places the page puts it:
//
//   <BillingNotices>  — the news, at the top: a switch the merchant started
//                       and has not approved on Shopify, a switch that ended
//                       without approval (declined or expired), and a
//                       claimed Enterprise agreement still billed on the old
//                       plan. A merchant back from Shopify reads the outcome
//                       first.
//   <PlanAction>      — inside the plan card, under the offer: switch
//                       interval or move band (an approved subscription),
//                       subscribe at the toggled interval (nothing approved
//                       yet), or contact sales (a band with no price).
//
// Dumb on purpose: the page owns the mutations; these render state and raise
// callbacks, so the label matrix is testable without a query harness.
//
// Nothing here states a change as settled. Until the merchant approves on
// Shopify their plan and price are what they were, and an effective date is
// quoted only from `takes_effect` (src/billing/planChange.takesEffect).

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertTitle, Button, Stack, Typography } from '@mui/material';

import { useFormatters } from '../../hooks/useFormatters';
import { usd } from '../../billing/money';
import { takesEffect } from '../../billing/planChange';
import { canChangePlan, canSubscribe } from '../../billing/planState';
import { planLabel } from './planCopy';
import ContactSalesButton from './ContactSalesButton';

/**
 * @param {object|null} pendingChange    the merchant's own pending switch
 *                                       (`merchantProposal(pending_tier_change)`)
 * @param {object|null} unapprovedChange `unapproved_change` off the status payload
 * @param {object|null} quotedMove       `quotedPendingMove(...)`
 * @param {string} currentPlanLabel      the plan the merchant is on, named
 * @param {string|null} renewalDate      `paid_until`
 * @param {Function} onWithdraw          "Keep my current plan"
 * @param {boolean} isWithdrawing
 * @param {boolean} isRedirecting        a hand-off to Shopify is under way
 */
export function BillingNotices({
  pendingChange, unapprovedChange, quotedMove, currentPlanLabel, renewalDate,
  onWithdraw, isWithdrawing, isRedirecting,
}) {
  if (!pendingChange && !unapprovedChange && !quotedMove) return null;
  return (
    <Stack spacing={1.5}>
      {pendingChange && (
        <PendingSwitch
          change={pendingChange}
          renewalDate={renewalDate}
          onWithdraw={onWithdraw}
          isWithdrawing={isWithdrawing}
          isRedirecting={isRedirecting}
        />
      )}
      {unapprovedChange && (
        <UnapprovedSwitch change={unapprovedChange} currentPlanLabel={currentPlanLabel} />
      )}
      {quotedMove && <QuotedAgreement move={quotedMove} />}
    </Stack>
  );
}

function PendingSwitch({ change, renewalDate, onWithdraw, isWithdrawing, isRedirecting }) {
  const { t } = useTranslation();
  const { formatNumber, formatDate } = useFormatters();

  const target = planLabel(
    t, formatNumber, change.tier_display, change.billing_interval, change.price,
  );
  const effect = takesEffect(change, renewalDate ? formatDate(renewalDate) : '');

  return (
    <Alert severity="info">
      <AlertTitle>{t('billing.planChange.pending.heading')}</AlertTitle>
      <Stack spacing={1}>
        <Typography variant="body2">{t('billing.planChange.pending.body', { plan: target })}</Typography>
        {effect && (
          <Typography variant="body2">
            {effect.kind === 'on_approval'
              ? t('billing.planChange.effectOnApproval')
              : t('billing.planChange.effectNextCycle', { date: effect.date })}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary">
          {t('billing.planChange.pending.ignored')}
        </Typography>
        <Stack direction="row" spacing={1}>
          {/* Unconditional: a proposal with no confirmation URL never reaches
              this component (`merchantProposal` answers null for one). */}
          <Button
            variant="contained"
            size="small"
            disabled={isWithdrawing || isRedirecting}
            onClick={() => window.location.assign(change.confirmation_url)}
          >
            {t('billing.planChange.approveCta')}
          </Button>
          {/* The app's half of "no": it asks Shopify nothing, so the pending
              charge lapses there on its own (billing.services.switching). */}
          <Button
            variant="outlined"
            size="small"
            disabled={isWithdrawing || isRedirecting}
            onClick={onWithdraw}
          >
            {t('billing.planChange.pending.withdrawCta')}
          </Button>
        </Stack>
      </Stack>
    </Alert>
  );
}

function UnapprovedSwitch({ change, currentPlanLabel }) {
  const { t } = useTranslation();
  const { formatNumber } = useFormatters();

  const attempted = planLabel(
    t, formatNumber, change.tier_display, change.billing_interval, change.price,
  );
  const declined = change.reason === 'declined';

  return (
    <Alert severity="warning">
      <AlertTitle>
        {declined
          ? t('billing.planChange.unapproved.headingDeclined')
          : t('billing.planChange.unapproved.headingExpired')}
      </AlertTitle>
      <Typography variant="body2">
        {declined
          ? t('billing.planChange.unapproved.bodyDeclined', { plan: attempted, current: currentPlanLabel })
          : t('billing.planChange.unapproved.bodyExpired', { plan: attempted, current: currentPlanLabel })}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {t('billing.planChange.unapproved.retry')}
      </Typography>
    </Alert>
  );
}

function QuotedAgreement({ move }) {
  const { t } = useTranslation();
  const { formatNumber, formatDate } = useFormatters();
  return (
    <Alert severity="info">
      <AlertTitle>
        {t('billing.plans.quotedBanner.heading', { price: usd(move.monthly, formatNumber) })}
      </AlertTitle>
      <Typography variant="body2">
        {move.nextChargeDate
          ? t('billing.plans.quotedBanner.billedOn', {
            plan: move.billedPlan, date: formatDate(move.nextChargeDate),
          })
          : t('billing.plans.quotedBanner.billedOnNoDate', { plan: move.billedPlan })}
      </Typography>
      <Typography variant="body2">{t('billing.plans.change.approvalNote')}</Typography>
    </Alert>
  );
}

/**
 * The card's action, rendered into its `.plan-card-cta`. A merchant with an
 * approved plan sees no toggle — the card shows the interval they pay — so
 * the buttons name the change: the other interval, or both intervals of a
 * band their revenue moved them to. A merchant with nothing approved picks
 * the interval on the card's toggle and Subscribes at it.
 *
 * @param {string} status             the billing status
 * @param {string} currentTier        the subscription's tier (switch only)
 * @param {string} currentInterval    the subscription's cadence (switch only)
 * @param {object} prices             `prices` off the pricing payload
 * @param {string} interval           'monthly' | 'annual', the card's toggle
 *                                    (subscribe only)
 * @param {boolean} parked            the merchant's own switch is waiting for
 *                                    approval — the notice above carries the
 *                                    only question on the table
 * @param {Function} onSwitch         called with 'monthly' | 'annual'
 * @param {Function} onSubscribe      called with 'monthly' | 'annual'
 * @param {boolean} isRedirecting     a hand-off to Shopify is under way: every
 *                                    button disables, the clicked one spins
 */
export function PlanAction({
  status, currentTier, currentInterval, prices, interval, parked, onSwitch, onSubscribe,
  isRedirecting,
}) {
  const { t } = useTranslation();
  const { formatNumber } = useFormatters();
  const [clicked, setClicked] = useState(null);

  if (parked) return null;
  const changes = canChangePlan(status);
  const subscribes = canSubscribe(status);
  if (!changes && !subscribes) return null;

  // A band with no listed price (Enterprise, not quoted yet): nothing can be
  // pushed, so the way forward is sales — never a dead end.
  if (prices.contact_sales) {
    return (
      <>
        <p className="plan-card-cta-body">{t('billing.plans.contactSalesBody')}</p>
        <ContactSalesButton salesEmail={prices.sales_email} />
      </>
    );
  }

  const monthly = usd(prices.monthly, formatNumber);
  const annual = usd(prices.annual, formatNumber);
  let actions;
  let raise = onSwitch;
  if (!changes) {
    raise = onSubscribe;
    actions = [interval === 'annual'
      ? { interval: 'annual', label: t('billing.subscribeTotalYear', { total: annual }) }
      : { interval: 'monthly', label: t('billing.subscribeTotalMonth', { total: monthly }) }];
  } else if (prices.tier !== currentTier) {
    actions = [
      { interval: 'monthly', label: t('billing.plans.change.moveTierMonthly', { plan: prices.tier_display, total: monthly }) },
      { interval: 'annual', label: t('billing.plans.change.moveTierAnnual', { plan: prices.tier_display, total: annual }) },
    ];
  } else if (currentInterval === 'annual') {
    actions = [
      { interval: 'monthly', label: t('billing.plans.change.switchToMonthly', { plan: prices.tier_display, total: monthly }) },
    ];
  } else {
    actions = [
      { interval: 'annual', label: t('billing.plans.change.switchToAnnual', { plan: prices.tier_display, total: annual }) },
    ];
  }

  return (
    <>
      {actions.map(({ interval: value, label }) => (
        <Button
          key={value}
          variant="contained"
          disabled={isRedirecting}
          loading={isRedirecting && clicked === value}
          onClick={() => { setClicked(value); raise(value); }}
        >
          {label}
        </Button>
      ))}
      <p className="plan-card-cta-note">{t('billing.plans.change.approvalNote')}</p>
    </>
  );
}
